import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import toast from 'react-hot-toast';

export default function CustomerWallet() {
  const nav = useNavigate();
  const [wallet, setWallet] = useState({ walletBalance: 0, walletTransactions: [] });
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [pixData, setPixData] = useState(null);
  const [pendingTx, setPendingTx] = useState(null);
  const [polling, setPolling] = useState(false);

  const loadWallet = () => {
    setLoading(true);
    api.get('/customer/wallet')
      .then(response => {
        setWallet(response.data);
        // Verifica se há transação pendente na lista
        const pending = (response.data.walletTransactions || []).find(t => t.status === 'pending');
        if (pending && !pixData) {
          setPendingTx(pending);
          setPixData({ qr: pending.pixQrCode, code: pending.pixCode, txId: pending.id, amount: pending.amount });
          setPolling(true);
        }
      })
      .catch(() => toast.error('Erro ao carregar carteira'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadWallet(); }, []);

  // Polling para confirmar PIX de adicionar saldo
  useEffect(() => {
    if (!polling || !pendingTx) return;
    const iv = setInterval(async () => {
      try {
        const r = await api.get(`/customer/wallet/topup/${pendingTx.id}/status`);
        if (r.data.status === 'paid') {
          clearInterval(iv);
          setWallet(prev => ({ ...prev, walletBalance: r.data.walletBalance }));
          setPixData(null);
          setPendingTx(null);
          setPolling(false);
          toast.success(`R$ ${r.data.transaction.amount.toFixed(2)} adicionados com sucesso!`);
          loadWallet();
        }
      } catch {}
    }, 3000);
    return () => clearInterval(iv);
  }, [polling, pendingTx]);

  const topup = async () => {
    const numericAmount = Number(amount);
    if (!numericAmount || numericAmount <= 0) {
      toast.error('Informe um valor válido'); return;
    }

    setAdding(true);
    try {
      const response = await api.post('/customer/wallet/topup', { amount: numericAmount });
      const data = response.data;

      if (data.pixQrCode) {
        setPixData({ qr: data.pixQrCode, code: data.pixCode, txId: data.transaction.id, amount: numericAmount });
        setPendingTx(data.transaction);
        setPolling(true);
        toast.success('PIX gerado! Escaneie para adicionar saldo');
      } else {
        toast.success('Saldo adicionado');
        loadWallet();
      }
      setAmount('');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao gerar PIX');
    } finally {
      setAdding(false);
    }
  };

  const cancelPix = () => {
    setPixData(null);
    setPendingTx(null);
    setPolling(false);
  };

  return (
    <div className="screen bg-gray-50 flex flex-col">
      <div className="gradient-hero px-6 pt-10 pb-6 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-dots opacity-30 pointer-events-none" />
        <div className="relative">
          <div className="flex justify-center mb-3">
            <img src="/logo.png" alt="GasNaPorta" className="h-14 w-auto object-contain logo-glow" />
          </div>
          <button onClick={() => nav('/cliente/home')} className="text-xl mb-2 block text-sky-200 hover:text-white">←</button>
          <h1 className="text-2xl font-black">Minha Carteira</h1>
          <p className="text-sky-200 text-sm mt-1">Adicione saldo via PIX e use nos pedidos</p>
        </div>
      </div>

      <div className="flex-1 px-4 py-5 overflow-y-auto space-y-4">
        {loading ? (
          <div className="flex justify-center py-12">
            <img src="/logo.png" alt="logo" className="w-16 h-16 object-contain animate-bounce" />
          </div>
        ) : (
          <>
            <div className="bg-gradient-to-br from-navy-600 to-navy-800 rounded-3xl p-6 text-white shadow-lg">
              <div className="text-sky-200 text-sm mb-1">Saldo disponível</div>
              <div className="text-5xl font-black">R$ {wallet.walletBalance?.toFixed(2)}</div>
              <div className="text-sky-200 text-xs mt-2">Use este saldo para pagar pedidos com mais rapidez</div>
            </div>

            {/* PIX QR Code */}
            {pixData ? (
              <div className="bg-white rounded-2xl p-5 shadow-sm text-center fade-in">
                <div className="text-sm font-bold text-gray-700 mb-3">Escaneie o QR Code para adicionar R$ {pixData.amount.toFixed(2)}</div>
                <div className="bg-white rounded-xl p-4 inline-block border border-gray-200">
                  <img src={pixData.qr} alt="QR Code PIX" className="w-48 h-48 object-contain" />
                </div>
                <div className="bg-gray-50 rounded-xl p-3 text-xs font-mono text-gray-600 break-all mt-3">
                  {pixData.code}
                </div>
                <div className="flex gap-2 justify-center mt-3">
                  <button
                    onClick={() => { navigator.clipboard.writeText(pixData.code); toast.success('Código copiado!'); }}
                    className="text-xs btn-sky px-4 py-2 rounded-full font-bold"
                  >
                    Copiar código PIX
                  </button>
                  <button
                    onClick={cancelPix}
                    className="text-xs bg-gray-100 text-gray-600 px-4 py-2 rounded-full font-bold"
                  >
                    Fechar
                  </button>
                </div>
                <div className="mt-3 text-xs text-gray-500">
                  Aguardando pagamento...
                  <span className="inline-block ml-1 animate-spin">⏳</span>
                </div>
                <div className="mt-2 text-[11px] text-gray-400">
                  Você pode sair desta tela. O saldo será creditado automaticamente quando o PIX for confirmado.
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl p-4 shadow-sm">
                <h3 className="font-bold text-gray-800 mb-3">Adicionar saldo via PIX</h3>
                <div className="grid grid-cols-3 gap-2 mb-3">
                  {[50, 100, 150].map(value => (
                    <button
                      key={value}
                      onClick={() => setAmount(String(value))}
                      className="rounded-xl bg-navy-50 text-navy-700 font-bold text-sm py-3 hover:bg-navy-100 transition-colors"
                    >
                      R$ {value}
                    </button>
                  ))}
                </div>
                <input
                  type="number"
                  min="1"
                  step="0.01"
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                  placeholder="Digite outro valor"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none mb-3"
                />
                <button
                  onClick={topup}
                  disabled={adding}
                  className="w-full py-4 btn-primary rounded-2xl font-bold text-white text-lg"
                >
                  {adding ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="animate-spin">⏳</span> Gerando PIX...
                    </span>
                  ) : 'Gerar PIX para adicionar saldo'}
                </button>
              </div>
            )}

            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <h3 className="font-bold text-gray-800 mb-3">Histórico da carteira</h3>
              {wallet.walletTransactions?.length === 0 && (
                <div className="text-sm text-gray-400 text-center py-6">Nenhuma movimentação ainda</div>
              )}
              <div className="space-y-2">
                {wallet.walletTransactions?.map(item => (
                  <div key={item.id} className="bg-gray-50 rounded-xl p-3 flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-sm text-gray-800">
                        {item.type === 'topup_pix' ? 'Adicionar saldo' : item.type === 'debit' ? 'Pagamento' : 'Movimentação'}
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(item.createdAt).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                        {' '}
                        <span className={`font-bold ${item.status === 'paid' ? 'text-green-600' : item.status === 'pending' ? 'text-yellow-600' : 'text-gray-400'}`}>
                          {item.status === 'paid' ? '✓ Confirmado' : item.status === 'pending' ? '⏳ Pendente' : item.status}
                        </span>
                      </div>
                    </div>
                    <div className={`font-black ${item.type === 'debit' ? 'text-red-500' : 'text-emerald-600'}`}>
                      {item.type === 'debit' ? '−' : '+'} R$ {item.amount?.toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
